import React, { useEffect, useState } from 'react';
import { GraduationCap, Award, FileText, ExternalLink, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Formations = () => {
    const { t, language } = useLanguage();
    const [formations, setFormations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFormations = async () => {
            try {
                const response = await axios.get(`${API}/formations/public`);
                setFormations(response.data);
            } catch (error) {
                console.error('Error fetching formations:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchFormations();
    }, []);

    const diplomes = formations.filter(f => f.type === 'diplome');
    const certifications = formations.filter(f => f.type === 'certification');
    const autresFormations = formations.filter(f => f.type === 'formation');

    const getIcon = (type) => {
        switch (type) {
            case 'diplome':
                return <GraduationCap className="w-6 h-6" />;
            case 'certification':
                return <Award className="w-6 h-6" />;
            default:
                return <FileText className="w-6 h-6" />;
        }
    };

    const getColorClass = (type) => {
        switch (type) {
            case 'diplome':
                return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'certification':
                return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
            default:
                return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
        }
    };

    const FormationCard = ({ formation }) => (
        <div 
            className="glass-card p-6 card-hover group"
            data-testid={`formation-${formation.id}`}
        >
            <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${getColorClass(formation.type)}`}>
                    {getIcon(formation.type)}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getColorClass(formation.type)}`}>
                            {formation.type === 'diplome' ? t('Diplôme', 'Degree') : 
                             formation.type === 'certification' ? 'Certification' : 'Formation'}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-slate-500">
                            <Calendar size={14} />
                            {formation.annee}
                        </span>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">
                        {language === 'en' && formation.titre_en ? formation.titre_en : formation.titre}
                    </h3>
                    <p className="text-slate-400 mb-3">{formation.organisme}</p>
                    <p className="text-white font-medium text-sm">
                        {language === 'en' && formation.description_en ? formation.description_en : formation.description}
                    </p>
                    {formation.document_url && (
                        <a 
                            href={formation.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-4 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                            data-testid={`formation-doc-${formation.id}`}
                        >
                            <ExternalLink size={16} />
                            {t('Voir le document', 'View document')}
                        </a>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <main data-testid="formations-page" className="min-h-screen pt-24">
            {/* Header */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                            {t('Formations & Certifications', 'Education & Certifications')}
                        </h1>
                        <p className="text-lg text-slate-400">
                            {t(
                                'Mon parcours académique et mes certifications professionnelles qui attestent de mes compétences techniques et fonctionnelles.',
                                'My academic background and professional certifications that attest to my technical and functional skills.'
                            )}
                        </p>
                    </div>
                </div>
            </section>

            {/* Diplômes */}
            {diplomes.length > 0 && (
                <section className="pb-16" data-testid="diplomes-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
                                <GraduationCap className="w-6 h-6" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-semibold text-white">
                                {t('Diplômes', 'Degrees')}
                            </h2>
                        </div>
                        <div className="grid gap-6">
                            {diplomes.map(formation => (
                                <FormationCard key={formation.id} formation={formation} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Certifications */}
            {certifications.length > 0 && (
                <section className="pb-16" data-testid="certifications-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 rounded-xl bg-violet-500/20 text-violet-400">
                                <Award className="w-6 h-6" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-semibold text-white">
                                Certifications
                            </h2>
                        </div>
                        <div className="grid gap-6">
                            {certifications.map(formation => (
                                <FormationCard key={formation.id} formation={formation} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Autres Formations */}
            {autresFormations.length > 0 && (
                <section className="pb-24" data-testid="autres-formations-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 rounded-xl bg-cyan-500/20 text-cyan-400">
                                <FileText className="w-6 h-6" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-semibold text-white">
                                {t('Autres Formations', 'Other Training')}
                            </h2>
                        </div>
                        <div className="grid gap-6">
                            {autresFormations.map(formation => (
                                <FormationCard key={formation.id} formation={formation} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Skills Summary */}
            <section className="py-16 border-t border-white/10" data-testid="skills-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <h2 className="text-2xl md:text-3xl font-semibold text-white mb-8">
                        {t('Compétences techniques', 'Technical skills')}
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="glass-card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">{t('Langages', 'Languages')}</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Java/J2EE', 'Python', 'JavaScript', 'SQL/PLSQL', 'VBA', 'TypeScript'].map(tech => (
                                    <span key={tech} className="tech-badge">{tech}</span>
                                ))}
                            </div>
                        </div>
                        <div className="glass-card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Frameworks</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Spring', 'React', 'React Native', 'JSF', 'Hibernate'].map(tech => (
                                    <span key={tech} className="tech-badge">{tech}</span>
                                ))}
                            </div>
                        </div>
                        <div className="glass-card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">{t('Bases de données', 'Databases')}</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Oracle', 'MongoDB', 'PostgreSQL', 'MySQL', 'Cosmos DB'].map(tech => (
                                    <span key={tech} className="tech-badge">{tech}</span>
                                ))}
                            </div>
                        </div>
                        <div className="glass-card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">{t('Outils', 'Tools')}</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Jira', 'Confluence', 'Postman', 'Git', 'Syndigo', 'Draw.io'].map(tech => (
                                    <span key={tech} className="tech-badge">{tech}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};
